import { HttpMethod } from '@vision/ui/enums';
import { TicketManagerApiRequest } from '../ticket-manager.api';

const TICKET_MANAGER_INNER_LOOP_ENDPOINT = '/v2/inner_loop';

interface TicketManagerTicketChangePriorityRequestDto {
  priority: string;
}

interface TicketManagerTicketChangeOwnerRequestDto {
  owner_id: string;
  owner_type: 'TicketTeam';
}

interface TicketManagerTicketChangePoolRequestDto {
  ticket_pool_id: string;
}

interface TicketManagerTicketAssignRequestDto {
  user_id: string;
}

interface TicketManagerTicketChangeStatusRequestDto {
  status: string;
}

interface TicketManagerTicketRootCauseRequestDto {
  root_cause_id: string;
}

interface TicketManagerTicketChangeRootCauseGroupRequestDto {
  root_cause_group_id: string;
}

export const buildTicketManagerTicketDetailRequest = ({
  projectId,
  ticketId,
}: {
  projectId: string;
  ticketId: string;
}): TicketManagerApiRequest => ({
  method: HttpMethod.GET,
  url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/tickets/${ticketId}/show`,
});

export const buildTicketAssignableUsersRequest = ({
  projectId,
  ticketId,
}: {
  projectId: string;
  ticketId: string;
}): TicketManagerApiRequest => ({
  method: HttpMethod.GET,
  url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/tickets/${ticketId}/assignable_users`,
});

export const buildGetTicketRootCausesRequest = ({
  projectId,
  ticketId,
}: {
  projectId: string;
  ticketId: string;
}): TicketManagerApiRequest => ({
  method: HttpMethod.GET,
  url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/tickets/${ticketId}/available_root_causes`,
});

export const buildTicketManagerTicketChangePriorityRequest = ({
  priority,
  projectId,
  ticketId,
}: {
  priority: string;
  projectId: string;
  ticketId: string;
}): TicketManagerApiRequest => {
  const data: TicketManagerTicketChangePriorityRequestDto = {
    priority,
  };

  return {
    data,
    method: HttpMethod.POST,
    url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/tickets/${ticketId}/change_priority`,
  };
};

export const buildTicketManagerTicketChangeOwnerRequest = ({
  ownerId,
  ownerType,
  projectId,
  ticketId,
}: {
  ownerId: string;
  ownerType: 'TicketTeam';
  projectId: string;
  ticketId: string;
}): TicketManagerApiRequest => {
  const data: TicketManagerTicketChangeOwnerRequestDto = {
    owner_id: ownerId,
    owner_type: ownerType,
  };

  return {
    data,
    method: HttpMethod.POST,
    url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/tickets/${ticketId}/change_owner`,
  };
};

export const buildTicketManagerTicketChangePoolRequest = ({
  projectId,
  ticketId,
  ticketPoolId,
}: {
  projectId: string;
  ticketId: string;
  ticketPoolId: string;
}): TicketManagerApiRequest => {
  const data: TicketManagerTicketChangePoolRequestDto = {
    ticket_pool_id: ticketPoolId,
  };

  return {
    data,
    method: HttpMethod.POST,
    url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/tickets/${ticketId}/change_pool`,
  };
};

export const buildTicketManagerTicketAssignRequest = ({
  projectId,
  ticketId,
  userId,
}: {
  projectId: string;
  ticketId: string;
  userId: string;
}): TicketManagerApiRequest => {
  const data: TicketManagerTicketAssignRequestDto = {
    user_id: userId,
  };

  return {
    data,
    method: HttpMethod.POST,
    url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/tickets/${ticketId}/assign`,
  };
};

export const buildTicketManagerTicketChangeStatusRequest = ({
  projectId,
  status,
  ticketId,
}: {
  projectId: string;
  status: string;
  ticketId: string;
}): TicketManagerApiRequest => {
  const data: TicketManagerTicketChangeStatusRequestDto = {
    status,
  };

  return {
    data,
    method: HttpMethod.POST,
    url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/tickets/${ticketId}/change_status`,
  };
};

export const buildTicketManagerTicketChangeRootCauseGroupRequest = ({
  projectId,
  rootCauseGroupId,
  ticketId,
}: {
  projectId: string;
  rootCauseGroupId: string;
  ticketId: string;
}): TicketManagerApiRequest => {
  const data: TicketManagerTicketChangeRootCauseGroupRequestDto = {
    root_cause_group_id: rootCauseGroupId,
  };

  return {
    data,
    method: HttpMethod.POST,
    url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/tickets/${ticketId}/change_root_cause_group`,
  };
};

export const buildTicketManagerTicketAddRootCauseRequest = ({
  projectId,
  rootCauseId,
  ticketId,
}: {
  projectId: string;
  rootCauseId: string;
  ticketId: string;
}): TicketManagerApiRequest => {
  const data: TicketManagerTicketRootCauseRequestDto = {
    root_cause_id: rootCauseId,
  };

  return {
    data,
    method: HttpMethod.POST,
    url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/tickets/${ticketId}/root_causes`,
  };
};

export const buildTicketManagerTicketRemoveRootCauseRequest = ({
  projectId,
  rootCauseId,
  ticketId,
}: {
  projectId: string;
  rootCauseId: string;
  ticketId: string;
}): TicketManagerApiRequest => {
  const data: TicketManagerTicketRootCauseRequestDto = {
    root_cause_id: rootCauseId,
  };

  return {
    data,
    method: HttpMethod.POST,
    url: `${TICKET_MANAGER_INNER_LOOP_ENDPOINT}/${projectId}/tickets/${ticketId}/root_causes/${rootCauseId}/delete`,
  };
};
